// src/pages/ActionPlan.js
// HeartSync - Post-Test Action Framework
// Gated page: only accessible after completing the Marriage Readiness Test
// Route: /action-plan (must receive state from results page)

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronDown, ChevronUp, BookOpen, MessageSquare, Heart,
  AlertTriangle, Shield, Star, ArrowLeft, Download, Clock,
  CheckCircle, Lock, ChevronRight, Flame, Zap
} from 'lucide-react';

// ─── TRACK CONFIG ────────────────────────────────────────────────────────────
const TRACKS = {
  A: {
    code: 'A',
    label: 'Track A: Ready',
    emoji: '🟢',
    color: '#10b981',
    gradient: 'from-emerald-500 to-teal-600',
    bg: 'rgba(16, 185, 129, 0.12)',
    border: 'rgba(16, 185, 129, 0.35)',
    description: 'You demonstrate strong readiness for covenant marriage.',
    instruction: 'Begin your premarital discipleship journey.',
    scoreRange: '85–100%',
  },
  B: {
    code: 'B',
    label: 'Track B: Growth',
    emoji: '🟡',
    color: '#f59e0b',
    gradient: 'from-amber-500 to-orange-500',
    bg: 'rgba(245, 158, 11, 0.12)',
    border: 'rgba(245, 158, 11, 0.35)',
    description: 'You have a solid foundation with areas that need intentional growth.',
    instruction: 'Complete the 4–6 week guided growth pathway below.',
    scoreRange: '65–84%',
  },
  C: {
    code: 'C',
    label: 'Track C: Pause',
    emoji: '🔴',
    color: '#ef4444',
    gradient: 'from-red-500 to-rose-600',
    bg: 'rgba(239, 68, 68, 0.12)',
    border: 'rgba(239, 68, 68, 0.35)',
    description: 'Some areas require deeper healing before proceeding.',
    instruction: 'Engage individual counseling and the healing modules below.',
    scoreRange: 'Below 65%',
  },
};

// ─── 7 ACTION MODULES ─────────────────────────────────────────────────────────
const MODULES = [
  {
    id: 1,
    title: 'Motive Realignment Teaching',
    subtitle: 'Marriage Is Covenant, Not Escape',
    icon: '⚖️',
    tracks: ['A', 'B', 'C'],
    color: '#8b5cf6',
    scripture: {
      reference: 'Genesis 2:24',
      text: '"Therefore a man shall leave his father and his mother and hold fast to his wife, and they shall become one flesh."',
    },
    scriptures: [
      { ref: 'Malachi 2:14', text: `"But you say, 'Why does he not?' Because the LORD was witness between you and the wife of your youth, to whom you have been faithless, though she is your companion and your wife by covenant."` },
      { ref: 'Matthew 19:4–6', text: `"He answered, 'Have you not read that he who created them from the beginning made them male and female, and said, Therefore a man shall leave his father and his mother and hold fast to his wife, and the two shall become one flesh'? So they are no longer two but one flesh. What therefore God has joined together, let not man separate."` },
      { ref: 'Ephesians 5:25', text: '"Husbands, love your wives, as Christ loved the church and gave himself up for her."' },
    ],
    teaching: `Marriage was never designed to be an escape from loneliness, family pressure, financial instability, or the fear of being alone. It was instituted by God as a sacred covenant — a binding, purposeful union that mirrors the relationship between Christ and His Church.

When you enter marriage to escape something rather than to commit to someone, you import your unresolved issues into the relationship. The marriage becomes the container for all the pain you were hoping it would cure.

Ask yourself: Am I running toward this person or running away from my circumstances? The answer matters eternally.

A covenant says: "I choose you in every season." A contract says: "I'll stay as long as this works for me." God never designed a contractual marriage. He designed a covenantal one — one where both parties bring their whole, healed selves to the altar.`,
    reflectionQuestions: [
      'What are the honest, unspoken reasons I want to get married right now?',
      'Am I running from something — pressure, loneliness, fear — rather than running toward a person?',
      'If all external circumstances were removed (family pressure, finances, fear of being alone), would I still choose this person?',
      'Have I submitted my motives to God and trusted godly counsel with my honest answers?',
    ],
    prayerPoints: [
      'Lord, purify my motives. Let me not enter marriage as an escape but as a covenant.',
      'Give me the courage to acknowledge any fear or pressure driving this decision.',
      'Help me to love this person as a choice, not a need.',
      'Lord, guard me from entering into covenant prematurely or for the wrong reasons.',
    ],
    books: [
      { title: 'The Meaning of Marriage', author: 'Timothy Keller' },
      { title: 'Sacred Marriage', author: 'Gary Thomas' },
      { title: "Things I Wish I'd Known Before We Got Married", author: 'Gary Chapman' },
    ],
    challenge: '7-Day Motive Check: Every morning for 7 days, write one honest reason you are pursuing this marriage. On day 7, review all 7 and pray over them.',
  },
  {
    id: 2,
    title: 'Emotional Healing Track',
    subtitle: "Your Wound Is Not Your Spouse's Assignment",
    icon: '💙',
    tracks: ['B', 'C'],
    color: '#3b82f6',
    scripture: {
      reference: 'Psalm 147:3',
      text: '"He heals the brokenhearted and binds up their wounds."',
    },
    scriptures: [
      { ref: 'Isaiah 61:1', text: '"The Spirit of the Lord GOD is upon me, because the LORD has anointed me to bring good news to the poor; he has sent me to bind up the brokenhearted, to proclaim liberty to the captives, and the opening of the prison to those who are bound."' },
      { ref: '2 Corinthians 1:3–4', text: '"Blessed be the God and Father of our Lord Jesus Christ, the Father of mercies and God of all comfort, who comforts us in all our affliction, so that we may be able to comfort those who are in any affliction, with the comfort with which we ourselves are comforted by God."' },
      { ref: 'Romans 8:28', text: '"And we know that for those who love God all things work together for good, for those who are called according to his purpose."' },
    ],
    teaching: `One of the most damaging expectations in marriage is expecting your spouse to heal wounds that only God and intentional inner work can heal. When an unhealed person marries, they unconsciously assign their partner the role of therapist, savior, and comforter — a role no human being was designed to carry permanently.

Childhood trauma, past relationship wounds, rejection, abandonment, and grief are real. They are valid. But they require healing before marriage, not during it. A marriage partner is not equipped — nor obligated — to carry the weight of your unprocessed pain.

Enter marriage as a healed (or healing) person, not as a wounded person hoping your spouse will fix you. The work of healing belongs first to you and to God, then to a counselor, and then — in covenant — to your spouse who walks with you in ongoing growth.`,
    reflectionQuestions: [
      'What are the deepest wounds I carry from my past (family, relationships, trauma)?',
      'Have I sought healing for these — through prayer, counseling, or spiritual mentorship?',
      'Am I expecting my partner to provide something only God and inner work can give?',
      'Do I know how my wounds manifest as behaviors — anger, withdrawal, control, people-pleasing?',
    ],
    prayerPoints: [
      'Lord, reveal every wound I have tried to hide or ignore.',
      'Give me the courage to seek healing — in prayer, community, and counseling.',
      "Protect my future spouse from becoming a substitute for Your healing work.",
      'Teach me to receive love without weaponizing my wounds.',
    ],
    books: [
      { title: 'Emotionally Healthy Spirituality', author: 'Peter Scazzero' },
      { title: 'The Soul of Shame', author: 'Curt Thompson' },
      { title: 'Healing the Wounded Heart', author: 'Dan B. Allender' },
    ],
    challenge: '30-Day Emotional Maturity Challenge: Daily journaling on one emotional pattern or trigger per day. At the end of 30 days, identify the 5 patterns that appeared most often and bring them to a counselor or pastor.',
  },
  {
    id: 3,
    title: 'Doctrinal Foundation Building',
    subtitle: 'Savior vs Lord: Who Governs This Marriage?',
    icon: '📖',
    tracks: ['A', 'B', 'C'],
    color: '#f59e0b',
    scripture: {
      reference: 'Amos 3:3',
      text: '"Do two walk together, unless they have agreed to meet?"',
    },
    scriptures: [
      { ref: '2 Corinthians 6:14', text: '"Do not be unequally yoked with unbelievers. For what partnership has righteousness with lawlessness? Or what fellowship has light with darkness?"' },
      { ref: 'Deuteronomy 6:6–7', text: '"And these words that I command you today shall be on your heart. You shall teach them diligently to your children, and shall talk of them when you sit in your house, and when you walk by the way, and when you lie down, and when you rise."' },
      { ref: 'Colossians 3:18–19', text: '"Wives, submit to your husbands, as is fitting in the Lord. Husbands, love your wives, and do not be harsh with them."' },
    ],
    teaching: `A marriage built on doctrinal ambiguity is a marriage navigating without a map. When two people disagree on whether Jesus is Savior only or Lord of all — including their marriage — they will eventually arrive at different destinations.

"Doctrinal alignment" does not mean you share identical theological opinions on every minor issue. It means you share a common submission to Scripture as authority, a shared view of Christ's lordship, and a common framework for raising children, making decisions, managing finances, and handling conflict.

The question is not "Do we both believe in God?" The question is: "Do we both submit to God as Lord — even when it is inconvenient?"

Shared faith without shared submission is decoration, not foundation.`,
    reflectionQuestions: [
      'Do we both submit to Scripture as the final authority over our marriage?',
      'Have we discussed how we will raise our children spiritually?',
      'Are there major doctrinal areas of tension — and how have we resolved them?',
      'Does my partner hold Jesus as Savior only, or as Lord of every area of life?',
    ],
    prayerPoints: [
      'Lord, align us doctrinally before we enter covenant.',
      'Reveal any foundational disagreements we have avoided.',
      'Help us to build our home on the Word, not culture.',
      'Give us both a hunger for Your truth over personal preference.',
    ],
    books: [
      { title: 'Why Christian Marriage Matters', author: 'James Montgomery Boice' },
      { title: "What Did You Expect?", author: 'Paul David Tripp' },
      { title: 'Doctrine: What Christians Should Believe', author: 'Mark Driscoll & Gerry Breshears' },
    ],
    challenge: '14-Day Doctrine Discussion: For two weeks, have one 30-minute conversation per day working through these 5 topics — Authority of Scripture, Prayer life, Tithing & Finance, Role of the Church, and Parenting Philosophy.',
  },
  {
    id: 4,
    title: 'Conflict & Communication Training',
    subtitle: 'Correction Without Destruction',
    icon: '🗣️',
    tracks: ['A', 'B', 'C'],
    color: '#10b981',
    scripture: {
      reference: 'Ephesians 4:26',
      text: '"Be angry and do not sin; do not let the sun go down on your anger."',
    },
    scriptures: [
      { ref: 'Proverbs 15:1', text: '"A soft answer turns away wrath, but a harsh word stirs up anger."' },
      { ref: 'James 1:19', text: '"Know this, my beloved brothers: let every person be quick to hear, slow to speak, slow to anger."' },
      { ref: 'Colossians 4:6', text: '"Let your speech always be gracious, seasoned with salt, so that you may know how you ought to answer each person."' },
    ],
    teaching: `Every couple will experience conflict. The question is not whether you will argue — it is whether your arguments will produce understanding or damage.

Destructive conflict uses contempt, stonewalling, criticism, and defensiveness (what researcher John Gottman calls the Four Horsemen of relational apocalypse). Constructive conflict uses honesty, restraint, curiosity, and repair.

You must be able to correct your partner without destroying them. You must be able to receive correction without feeling attacked. This is a skill — and like all skills, it requires intentional development before and during marriage.

Couples who fight well stay together. Couples who fight dirty drift apart.`,
    reflectionQuestions: [
      'What is my default response to conflict — fight, flee, freeze, or appease?',
      'Have I seen healthy conflict modeled in my family of origin? If not, where am I learning it?',
      'Can I correct my partner with kindness, or do I become harsh when I feel wronged?',
      "What are the recurring conflict patterns in this relationship, and what do they reveal about each of us?",
    ],
    prayerPoints: [
      'Lord, tame my tongue in moments of anger.',
      'Teach me to listen to understand, not just to respond.',
      'Give us tools and language to navigate disagreement with grace.',
      'Help us to fight FOR our relationship, not AGAINST each other.',
    ],
    books: [
      { title: 'The Seven Principles for Making Marriage Work', author: 'John Gottman' },
      { title: 'Crucial Conversations', author: 'Kerry Patterson' },
      { title: 'Hold Me Tight', author: 'Sue Johnson' },
    ],
    challenge: 'Weekly Self-Check: Every Sunday, spend 20 minutes answering — (1) Was there a conflict this week? (2) How did I handle it? (3) What would I do differently? (4) What do I appreciate about how my partner handled it?',
    weeklyForm: true,
  },
  {
    id: 5,
    title: 'Toxicity Intervention Protocol',
    subtitle: 'Red Flags Are Not Suggestions',
    icon: '🚨',
    tracks: ['B', 'C'],
    color: '#ef4444',
    urgent: true,
    scripture: {
      reference: 'Proverbs 4:23',
      text: '"Keep your heart with all vigilance, for from it flow the springs of life."',
    },
    scriptures: [
      { ref: '1 Corinthians 15:33', text: '"Do not be deceived: Bad company ruins good morals."' },
      { ref: 'Proverbs 22:24–25', text: '"Make no friendship with a man given to anger, nor go with a wrathful man, lest you learn his ways and entangle yourself in a snare."' },
      { ref: '2 Timothy 3:1–5', text: '"But understand this, that in the last days there will come times of difficulty. For people will be lovers of self, lovers of money, proud, arrogant, abusive, disobedient to their parents, ungrateful, unholy, heartless, unappeasable, slanderous, without self-control, brutal, not loving good, treacherous, reckless, swollen with conceit, lovers of pleasure rather than lovers of God, having the appearance of godliness, but denying its power. Avoid such people."' },
    ],
    teaching: `Love does not erase red flags. It sometimes illuminates them.

If the Marriage Readiness Assessment revealed concerning patterns in Section 6 — such as controlling behavior, explosive anger, patterns of manipulation, chronic dishonesty, substance issues, or unresolved abusive tendencies — these require immediate pastoral intervention before any wedding planning continues.

Red flags are not personality quirks to manage. They are warning signals that demand attention. Dismissing them because of love, time invested, or family pressure is not faith — it is denial.

This module is not about suspicion or perfectionism. It is about wisdom. Godly wisdom protects you from entering covenant with a person who is not yet safe. Love can remain. The wedding date must wait.`,
    reflectionQuestions: [
      'Did the Toxicity Screening (Section 6) reveal any patterns I have been minimizing or explaining away?',
      'Have I shared the results of this assessment with my pastor or counselor?',
      'Do I feel emotionally safe with this person — to disagree, to be vulnerable, to say no?',
      'Am I staying in this relationship out of love or out of fear of leaving?',
    ],
    prayerPoints: [
      'Lord, give me eyes to see what love has blinded me to.',
      'Protect me from making a permanent covenant with unresolved danger.',
      'Give my pastor wisdom as I bring this before him.',
      'Lord, if this relationship needs to be paused or ended, give me the courage to act in wisdom.',
    ],
    books: [
      { title: 'Why Does He Do That?', author: 'Lundy Bancroft' },
      { title: 'Boundaries in Dating', author: 'Henry Cloud & John Townsend' },
      { title: 'Is It You, Me, or Adult ADD?', author: 'Stephanie Sarkis' },
    ],
    redFlagButton: true,
  },
  {
    id: 6,
    title: 'Identity Stabilization',
    subtitle: 'Marriage Is Addition, Not Identity',
    icon: '🪞',
    tracks: ['A', 'B', 'C'],
    color: '#6366f1',
    scripture: {
      reference: 'Galatians 2:20',
      text: '"I have been crucified with Christ. It is no longer I who live, but Christ who lives in me."',
    },
    scriptures: [
      { ref: '1 Corinthians 7:7', text: '"I wish that all were as I myself am. But each has his own gift from God, one of one kind and one of another."' },
      { ref: 'Psalm 139:13–14', text: '"For you formed my inward parts; you knitted me together in my mother\'s womb. I praise you, for I am fearfully and wonderfully made. Wonderful are your works; my soul knows it very well."' },
      { ref: 'Romans 12:2', text: '"Do not be conformed to this world, but be transformed by the renewal of your mind, that by testing you may discern what is the will of God, what is good and acceptable and perfect."' },
    ],
    teaching: `You cannot become yourself through your spouse. Identity that is sourced in another person is not identity — it is dependency.

A person who does not know who they are in Christ before marriage will expect their spouse to complete them, define them, and validate them. This is not a partnership — it is an assignment placed on a human being that only God can fulfill.

Know who you are before you pledge yourself to another. Know your calling. Know your values. Know your non-negotiables. Know your gifts. Know what you will not compromise and what you will sacrifice.

Marriage adds to identity. It does not create it.`,
    reflectionQuestions: [
      "Who am I apart from this relationship — my gifts, my calling, my values?",
      'Do I know what I believe, what I stand for, and what I will not compromise in marriage?',
      'Am I entering this relationship secure in God\'s love, or looking for this person to provide the security God should give?',
      'What parts of myself have I abandoned or suppressed to maintain this relationship?',
    ],
    prayerPoints: [
      'Lord, root my identity in Christ — not in this relationship.',
      'Show me who I am apart from this person.',
      'Help me to enter marriage as a whole person, not half of one.',
      'Protect me from losing myself in the pursuit of love.',
    ],
    books: [
      { title: 'The Search for Significance', author: 'Robert McGee' },
      { title: 'Boundaries', author: 'Henry Cloud & John Townsend' },
      { title: 'The Ragamuffin Gospel', author: 'Brennan Manning' },
    ],
    challenge: 'Identity Audit: Write down 10 things that are true about you that have nothing to do with your relationship — your gifts, your values, your calling, your dreams, your convictions. Share them with your pastor or mentor.',
  },
  {
    id: 7,
    title: 'Genetic & Practical Discussion',
    subtitle: 'Mandatory Conversations Before the Altar',
    icon: '🧬',
    tracks: ['A', 'B'],
    color: '#14b8a6',
    scripture: {
      reference: 'Proverbs 20:18',
      text: '"Plans are established by counsel; by wise guidance wage war."',
    },
    scriptures: [
      { ref: 'Luke 14:28', text: '"For which of you, desiring to build a tower, does not first sit down and count the cost, whether he has enough to complete it?"' },
      { ref: 'Proverbs 27:17', text: '"Iron sharpens iron, and one man sharpens another."' },
      { ref: 'Ecclesiastes 4:9', text: '"Two are better than one, because they have a good reward for their toil."' },
    ],
    teaching: `There are conversations that engaged couples avoid because they are uncomfortable. These conversations do not disappear after the wedding — they return, with greater weight and consequence.

Genetically, have both partners been tested for hereditary conditions relevant to your families? Are there mental health histories, chronic illnesses, or family medical patterns that should be part of a transparent pre-marital discussion?

Practically, have you discussed finances, debt, career direction, living arrangements, in-law dynamics, contraception, parenting philosophy, and household responsibilities?

These are not unromantic conversations. They are responsible ones. Wisdom says: count the cost before you build.`,
    reflectionQuestions: [
      'What hereditary or genetic conditions exist in each family that the other partner should know about?',
      'Have we had an honest conversation about debt, credit scores, spending habits, and financial goals?',
      'Have we agreed on where we will live, how we will handle in-law boundaries, and how decisions will be made?',
      'Have we discussed children — how many, when, how they will be raised, and what happens if we cannot conceive?',
    ],
    mandatoryTopics: [
      'Family medical history & genetic considerations',
      'Mental health history (both partners)',
      'Debt, savings, and financial expectations',
      'Career direction and sacrifices required',
      'Children — timeline, number, parenting philosophy',
      'In-law relationships and boundaries',
      'Living arrangements (whose city? whose home?)',
      'Household roles and responsibilities',
    ],
    prayerPoints: [
      'Lord, give us the courage to have the hard conversations before the altar.',
      'Help us to be transparent with each other, even when it is uncomfortable.',
      'Give us wisdom to plan for the future without fear.',
      'Let no hidden thing become a marriage-breaking surprise.',
    ],
    books: [
      { title: 'Before You Say "I Do"', author: 'Wes Roberts & H. Norman Wright' },
      { title: 'The DNA of Relationships', author: 'Gary Smalley' },
      { title: 'Fighting for Your Marriage', author: 'Howard Markman et al.' },
    ],
    challenge: 'The 7 Conversations: Schedule one 60-minute conversation per week for 7 weeks, each covering one of the mandatory topics above. After each conversation, pray together and write one insight.',
  },
];

// ─── HELPER ───────────────────────────────────────────────────────────────────
function getTrack(score) {
  if (score >= 85) return 'A';
  if (score >= 65) return 'B';
  return 'C';
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function ActionPlan() {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedModules, setExpandedModules] = useState({});
  const [activeTab, setActiveTab] = useState({});
  const [showRedFlagModal, setShowRedFlagModal] = useState(false);

  // ── GATE: require state from results page ──────────────────────────────────
  const state = location.state;
  useEffect(() => {
    if (!state || typeof state.score !== 'number' || !state.name) {
      navigate('/marriage-readiness-test', { replace: true });
    }
  }, [state, navigate]);

  if (!state || typeof state.score !== 'number') {
    return (
      <div className="min-h-screen" style={{ background: '#0a0a0f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#6b7280', textAlign: 'center' }}>
          <Lock size={40} style={{ margin: '0 auto 12px', color: '#374151' }} />
          <p style={{ fontFamily: 'Georgia, serif' }}>Complete the Marriage Readiness Test to access your Action Plan.</p>
          <button
            onClick={() => navigate('/marriage-readiness-test')}
            style={{ marginTop: 20, padding: '10px 24px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
          >
            Take the Test
          </button>
        </div>
      </div>
    );
  }

  const { score, name, sectionScores } = state;
  const trackCode = getTrack(score);
  const track = TRACKS[trackCode];

  const visibleModules = MODULES.filter(m => m.tracks.includes(trackCode));

  function toggleModule(id) {
    setExpandedModules(prev => ({ ...prev, [id]: !prev[id] }));
    if (!activeTab[id]) setActiveTab(prev => ({ ...prev, [id]: 'teaching' }));
  }

  function setTab(id, tab) {
    setActiveTab(prev => ({ ...prev, [id]: tab }));
  }

  // ── STYLES ─────────────────────────────────────────────────────────────────
  const S = {
    page: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a0f 0%, #0f0f1a 40%, #0d1117 100%)',
      color: '#e5e7eb',
      fontFamily: "'Georgia', 'Times New Roman', serif",
      paddingBottom: 80,
    },
    header: {
      background: 'linear-gradient(180deg, rgba(0,0,0,0.9) 0%, transparent 100%)',
      padding: '0 0 0 0',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
    },
    heroSection: {
      maxWidth: 760,
      margin: '0 auto',
      padding: '40px 20px 32px',
    },
    backBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      color: '#9ca3af',
      cursor: 'pointer',
      fontSize: 14,
      background: 'none',
      border: 'none',
      padding: 0,
      marginBottom: 28,
      fontFamily: 'inherit',
    },
    tagline: {
      fontSize: 11,
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      color: '#6b7280',
      marginBottom: 10,
    },
    heroTitle: {
      fontSize: 28,
      fontWeight: 700,
      color: '#f9fafb',
      lineHeight: 1.25,
      marginBottom: 6,
    },
    heroName: {
      color: track.color,
    },
    scoreRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      marginBottom: 24,
      flexWrap: 'wrap',
    },
    scorePill: {
      fontSize: 22,
      fontWeight: 700,
      color: track.color,
    },
    trackBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '5px 14px',
      borderRadius: 20,
      fontSize: 13,
      fontWeight: 600,
      background: track.bg,
      border: `1px solid ${track.border}`,
      color: track.color,
    },
    trackCard: {
      background: track.bg,
      border: `1px solid ${track.border}`,
      borderRadius: 14,
      padding: '20px 24px',
      marginBottom: 36,
    },
    trackCardTitle: {
      fontSize: 15,
      fontWeight: 700,
      color: track.color,
      marginBottom: 6,
    },
    trackCardDesc: {
      color: '#d1d5db',
      fontSize: 14,
      lineHeight: 1.65,
      marginBottom: 8,
    },
    trackInstruction: {
      fontSize: 13,
      color: '#9ca3af',
      fontStyle: 'italic',
    },
    // modules
    moduleWrap: {
      maxWidth: 760,
      margin: '0 auto',
      padding: '0 20px',
    },
    sectionLabel: {
      fontSize: 11,
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
      color: '#4b5563',
      marginBottom: 16,
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },
    moduleCard: (m, open) => ({
      background: open
        ? `linear-gradient(135deg, ${m.color}14 0%, rgba(15,15,25,0.95) 60%)`
        : 'rgba(15, 15, 24, 0.7)',
      border: `1px solid ${open ? m.color + '40' : 'rgba(255,255,255,0.07)'}`,
      borderRadius: 14,
      marginBottom: 12,
      overflow: 'hidden',
      transition: 'border-color 0.25s',
    }),
    moduleHeader: (m) => ({
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      padding: '18px 20px',
      cursor: 'pointer',
      userSelect: 'none',
    }),
    moduleIcon: (m) => ({
      width: 42,
      height: 42,
      borderRadius: 10,
      background: `${m.color}22`,
      border: `1px solid ${m.color}40`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 20,
      flexShrink: 0,
    }),
    moduleTitle: {
      fontSize: 15,
      fontWeight: 700,
      color: '#f3f4f6',
      marginBottom: 2,
    },
    moduleSubtitle: {
      fontSize: 12,
      color: '#6b7280',
      fontStyle: 'italic',
    },
    urgentTag: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      fontSize: 10,
      fontWeight: 700,
      color: '#fca5a5',
      background: 'rgba(239,68,68,0.15)',
      border: '1px solid rgba(239,68,68,0.3)',
      borderRadius: 6,
      padding: '2px 8px',
      marginLeft: 8,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
    moduleBody: {
      padding: '0 20px 22px',
    },
    // Scripture callout
    scriptureCard: (m) => ({
      background: `${m.color}0f`,
      border: `1px solid ${m.color}28`,
      borderLeft: `3px solid ${m.color}`,
      borderRadius: '0 10px 10px 0',
      padding: '14px 18px',
      marginBottom: 20,
    }),
    scriptureRef: (m) => ({
      fontSize: 11,
      fontWeight: 700,
      color: m.color,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      marginBottom: 6,
    }),
    scriptureText: {
      fontSize: 14,
      color: '#d1d5db',
      lineHeight: 1.7,
      fontStyle: 'italic',
    },
    // Tabs
    tabRow: {
      display: 'flex',
      gap: 4,
      marginBottom: 20,
      flexWrap: 'wrap',
    },
    tab: (active, m) => ({
      padding: '6px 14px',
      borderRadius: 8,
      fontSize: 12,
      fontWeight: 600,
      cursor: 'pointer',
      border: `1px solid ${active ? m.color + '60' : 'rgba(255,255,255,0.1)'}`,
      background: active ? `${m.color}20` : 'transparent',
      color: active ? m.color : '#6b7280',
      transition: 'all 0.18s',
      fontFamily: 'inherit',
    }),
    teachingText: {
      fontSize: 14,
      color: '#d1d5db',
      lineHeight: 1.85,
      whiteSpace: 'pre-line',
    },
    reflectionList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    reflectionItem: {
      display: 'flex',
      gap: 10,
      marginBottom: 14,
      alignItems: 'flex-start',
    },
    reflectionNum: (m) => ({
      flexShrink: 0,
      width: 22,
      height: 22,
      borderRadius: '50%',
      background: `${m.color}22`,
      border: `1px solid ${m.color}44`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 11,
      fontWeight: 700,
      color: m.color,
      marginTop: 1,
    }),
    reflectionText: {
      fontSize: 14,
      color: '#d1d5db',
      lineHeight: 1.65,
    },
    prayerItem: {
      display: 'flex',
      gap: 10,
      marginBottom: 13,
      alignItems: 'flex-start',
    },
    prayerDot: (m) => ({
      flexShrink: 0,
      width: 6,
      height: 6,
      borderRadius: '50%',
      background: m.color,
      marginTop: 7,
    }),
    prayerText: {
      fontSize: 14,
      color: '#d1d5db',
      lineHeight: 1.65,
      fontStyle: 'italic',
    },
    bookList: {
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    },
    bookItem: (m) => ({
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      padding: '12px 14px',
      background: `${m.color}0a`,
      border: `1px solid ${m.color}20`,
      borderRadius: 10,
    }),
    bookIcon: (m) => ({
      color: m.color,
      flexShrink: 0,
      marginTop: 1,
    }),
    bookTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: '#f3f4f6',
    },
    bookAuthor: {
      fontSize: 12,
      color: '#6b7280',
    },
    challengeBox: (m) => ({
      background: `${m.color}10`,
      border: `1px dashed ${m.color}40`,
      borderRadius: 10,
      padding: '14px 18px',
      marginTop: 4,
    }),
    challengeLabel: (m) => ({
      fontSize: 10,
      fontWeight: 700,
      color: m.color,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      marginBottom: 6,
      display: 'flex',
      alignItems: 'center',
      gap: 5,
    }),
    challengeText: {
      fontSize: 13,
      color: '#d1d5db',
      lineHeight: 1.7,
    },
    mandatoryList: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
      gap: 8,
      marginTop: 8,
    },
    mandatoryItem: (m) => ({
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '9px 12px',
      background: `${m.color}0d`,
      border: `1px solid ${m.color}22`,
      borderRadius: 8,
      fontSize: 12,
      color: '#d1d5db',
    }),
    redFlagBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '12px 22px',
      background: 'linear-gradient(135deg, rgba(239,68,68,0.2), rgba(239,68,68,0.1))',
      border: '1px solid rgba(239,68,68,0.45)',
      borderRadius: 10,
      color: '#fca5a5',
      cursor: 'pointer',
      fontSize: 13,
      fontWeight: 600,
      fontFamily: 'inherit',
      marginTop: 16,
      transition: 'all 0.2s',
    },
    // Modal
    modalOverlay: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.8)',
      backdropFilter: 'blur(4px)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    },
    modalBox: {
      background: 'linear-gradient(135deg, #1a0a0a, #0f0f1a)',
      border: '1px solid rgba(239,68,68,0.4)',
      borderRadius: 16,
      padding: '32px 28px',
      maxWidth: 480,
      width: '100%',
    },
    divider: {
      height: 1,
      background: 'rgba(255,255,255,0.05)',
      margin: '24px 0',
    },
  };

  // TABS
  const TABS = [
    { id: 'teaching', label: '📝 Teaching' },
    { id: 'reflection', label: '🔍 Reflection' },
    { id: 'prayer', label: '🙏 Prayer' },
    { id: 'scripture', label: '📖 Scripture' },
    { id: 'books', label: '📚 Books' },
    { id: 'action', label: '⚡ Action' },
  ];

  function renderTab(m, tab) {
    switch (tab) {
      case 'teaching':
        return <p style={S.teachingText}>{m.teaching}</p>;

      case 'reflection':
        return (
          <ul style={S.reflectionList}>
            {m.reflectionQuestions.map((q, i) => (
              <li key={i} style={S.reflectionItem}>
                <div style={S.reflectionNum(m)}>{i + 1}</div>
                <p style={S.reflectionText}>{q}</p>
              </li>
            ))}
          </ul>
        );

      case 'prayer':
        return (
          <div>
            {m.prayerPoints.map((pp, i) => (
              <div key={i} style={S.prayerItem}>
                <div style={S.prayerDot(m)} />
                <p style={S.prayerText}>{pp}</p>
              </div>
            ))}
            {m.weeklyForm && (
              <div style={{ ...S.challengeBox(m), marginTop: 20 }}>
                <div style={S.challengeLabel(m)}><CheckCircle size={12} /> Weekly Self-Check Form</div>
                <p style={S.challengeText}>
                  Every Sunday: (1) Was there a conflict this week? (2) How did I handle it? (3) What would I do differently? (4) What do I appreciate about how my partner handled it?
                </p>
              </div>
            )}
          </div>
        );

      case 'scripture':
        return (
          <div>
            {m.scriptures.map((s, i) => (
              <div key={i} style={{ ...S.scriptureCard(m), marginBottom: 12 }}>
                <div style={S.scriptureRef(m)}>{s.ref}</div>
                <p style={S.scriptureText}>{s.text}</p>
              </div>
            ))}
          </div>
        );

      case 'books':
        return (
          <div style={S.bookList}>
            {m.books.map((b, i) => (
              <div key={i} style={S.bookItem(m)}>
                <BookOpen size={16} style={S.bookIcon(m)} />
                <div>
                  <p style={S.bookTitle}>{b.title}</p>
                  <p style={S.bookAuthor}>{b.author}</p>
                </div>
              </div>
            ))}
          </div>
        );

      case 'action':
        return (
          <div>
            {m.challenge && (
              <div style={S.challengeBox(m)}>
                <div style={S.challengeLabel(m)}><Zap size={12} /> Challenge</div>
                <p style={S.challengeText}>{m.challenge}</p>
              </div>
            )}
            {m.mandatoryTopics && (
              <div style={{ marginTop: m.challenge ? 16 : 0 }}>
                <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 10 }}>Mandatory conversation topics:</p>
                <div style={S.mandatoryList}>
                  {m.mandatoryTopics.map((t, i) => (
                    <div key={i} style={S.mandatoryItem(m)}>
                      <CheckCircle size={13} style={{ color: m.color, flexShrink: 0 }} />
                      {t}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {m.redFlagButton && (
              <button style={S.redFlagBtn} onClick={() => setShowRedFlagModal(true)}>
                <AlertTriangle size={15} />
                Report a Red Flag to Pastor
              </button>
            )}
          </div>
        );

      default: return null;
    }
  }

  return (
    <div style={S.page}>
      {/* HEADER */}
      <div style={S.header}>
        <div style={S.heroSection}>
          <button style={S.backBtn} onClick={() => navigate(-1)}>
            <ArrowLeft size={14} /> Back to Results
          </button>

          <div style={S.tagline}>HeartSync · Post-Assessment Action Plan</div>
          <h1 style={S.heroTitle}>
            <span style={S.heroName}>{name}'s</span> Pathway Forward
          </h1>

          <div style={S.scoreRow}>
            <span style={S.scorePill}>{score}%</span>
            <span style={S.trackBadge}>
              {track.emoji} {track.label}
            </span>
            <span style={{ fontSize: 13, color: '#4b5563' }}>Score Range: {track.scoreRange}</span>
          </div>

          <div style={S.trackCard}>
            <div style={S.trackCardTitle}>{track.emoji} {track.label}</div>
            <p style={S.trackCardDesc}>{track.description}</p>
            <p style={S.trackInstruction}>{track.instruction}</p>
          </div>

          {/* Track selector key */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
            {Object.values(TRACKS).map(t => (
              <div key={t.code} style={{
                display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
                opacity: t.code === trackCode ? 1 : 0.35,
              }}>
                <span>{t.emoji}</span>
                <span style={{ color: t.color }}>{t.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* MODULES */}
      <div style={S.moduleWrap}>
        <div style={S.sectionLabel}>
          <Flame size={12} />
          Your Action Modules ({visibleModules.length})
        </div>

        {visibleModules.map(m => {
          const open = !!expandedModules[m.id];
          const tab = activeTab[m.id] || 'teaching';

          return (
            <div key={m.id} style={S.moduleCard(m, open)}>
              {/* Header Row */}
              <div style={S.moduleHeader(m)} onClick={() => toggleModule(m.id)}>
                <div style={S.moduleIcon(m)}>{m.icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                    <span style={S.moduleTitle}>{m.title}</span>
                    {m.urgent && (
                      <span style={S.urgentTag}><AlertTriangle size={9} /> Priority</span>
                    )}
                  </div>
                  <div style={S.moduleSubtitle}>"{m.subtitle}"</div>
                </div>
                {open ? <ChevronUp size={16} color="#4b5563" /> : <ChevronDown size={16} color="#4b5563" />}
              </div>

              {/* Expanded Body */}
              {open && (
                <div style={S.moduleBody}>
                  {/* Key Scripture */}
                  <div style={S.scriptureCard(m)}>
                    <div style={S.scriptureRef(m)}>{m.scripture.reference}</div>
                    <p style={S.scriptureText}>{m.scripture.text}</p>
                  </div>

                  {/* Tab Nav */}
                  <div style={S.tabRow}>
                    {TABS.map(t => (
                      <button key={t.id} style={S.tab(tab === t.id, m)} onClick={() => setTab(m.id, t.id)}>
                        {t.label}
                      </button>
                    ))}
                  </div>

                  {/* Tab Content */}
                  {renderTab(m, tab)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* RED FLAG MODAL */}
      {showRedFlagModal && (
        <div style={S.modalOverlay} onClick={() => setShowRedFlagModal(false)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <AlertTriangle size={22} color="#ef4444" />
              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#f9fafb', margin: 0 }}>Red Flag Report</h3>
            </div>
            <p style={{ fontSize: 14, color: '#d1d5db', lineHeight: 1.7, marginBottom: 20 }}>
              If you have observed serious concerns — controlling behavior, threats, emotional abuse, explosive anger, or any pattern that makes you feel unsafe — your pastor needs to know.
            </p>
            <p style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.65, marginBottom: 24 }}>
              This is a confidential pastoral concern. Sending this will notify <strong style={{ color: '#f3f4f6' }}>Pastor Adeniyi</strong> directly. You are not alone in this.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                style={{
                  flex: 1, padding: '11px 0', background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                  border: 'none', borderRadius: 9, color: 'white', fontWeight: 700,
                  fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
                }}
                onClick={() => {
                  // Here you'd trigger your email/cloud function
                  alert('Your concern has been sent to the pastor. You will be contacted within 24 hours.');
                  setShowRedFlagModal(false);
                }}
              >
                Send to Pastor
              </button>
              <button
                style={{
                  padding: '11px 20px', background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)', borderRadius: 9,
                  color: '#9ca3af', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14,
                }}
                onClick={() => setShowRedFlagModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
